import React, { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { Button, Card, CardBody, CardHeader, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, useDisclosure } from "@nextui-org/react";
import ControlledInput from "../../../../elements/ControlledInput";
import toast from "react-hot-toast";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { isResponseErrorObject, isiKolomDariPerusahaan } from "../../../../../services/helpers";
import useSWR from "swr";
import { getFetcher, postFetcher } from "../../../../../services/api";
import ControlledReactSelect from "../../../../elements/ControlledReactSelect";

export default function InlineEditTujuanBongkar({ parentIndex, companyId, control, setValue }) {
  const [filteredDetail, setFilteredDetail] = useState({});
  const { fields, append, remove } = useFieldArray({ control, name: `perusahaanAsalMuatDanTujuanBongkar[${parentIndex}].perusahaanTujuanBongkar` });
  const { isOpen: isOpenModalForm, onOpen: onOpenModalForm, onOpenChange: onOpenChangeModalForm, onClose: onCloseModalForm } = useDisclosure();
  const { data: dataTujuanBongkar, isLoading: isLoadingTujuanBongkar, mutate: mutateTujuanBongkar } = useSWR(companyId ? `/api/company/search-perusahaan-tujuan-bongkar?companyId=${companyId}&returnAll=true` : null, getFetcher);
  const { data: dataAsalMuat, isLoading: isLoadingAsalMuat } = useSWR(companyId ? `/api/company/search-perusahaan-asal-muat?companyId=${companyId}&returnAll=true` : null, getFetcher);
  const {
    register: registerInline,
    control: controlInline,
    setValue: setValueInline,
    handleSubmit: handleSubmitInline,
    watch: watchInline,
    reset: resetInline,
    formState: { errors: errorsInline, isSubmitting: isSubmittingInline },
  } = useForm();
  const provinceId = watchInline("provinceId");
  const regencyId = watchInline("regencyId");
  const districtId = watchInline("districtId");

  // Select lokasi NextUI dipasangi register() biasa, jadi nilainya TIDAK ikut
  // berubah saat diisi lewat setValue - tampilannya tetap "Pilih..." padahal
  // nilainya sudah ada di form. selectedKeys dijahit ke nilai form supaya
  // pengisian otomatis dari data perusahaan benar-benar terlihat.
  const nilaiFormInline = useWatch({ control: controlInline });
/*
   * Daftar pilihan perusahaan tujuan bongkar.
   *
   * Bukan isian bebas: hanya perusahaan yang sudah tertaut SIMPEL dan BUKAN
   * importir. Pengecualiannya ketat - perusahaan yang merangkap importir dan
   * distributor pun tidak muncul - jadi dipakai tipePerusahaanExclude, bukan
   * daftar tipe yang diizinkan.
   */
  // Dua syarat sekaligus, bukan sekadar pengecualian: harus PUNYA salah satu
  // tipe selain impor, lalu yang punya tipe impor dibuang. Tanpa syarat
  // pertama, perusahaan yang kolom tipePerusahaan-nya kosong ikut lolos -
  // 3.886 baris, bukan 2.215.
  const TIPE_DIIZINKAN = ['Produksi B3', 'Distribusi B3', 'Menggunakan B3', 'Mengangkut B3'].join(',')
  const TIPE_DIKECUALIKAN = 'Impor B3'
  const { data: dataTujuanTerdaftar, isLoading: isLoadingTujuanTerdaftar } = useSWR(
      `/api/company/search-company?returnAll=true&hasIdCompanySimpel=true` +
      `&tipePerusahaan=${encodeURIComponent(TIPE_DIIZINKAN)}` +
      `&tipePerusahaanExclude=${encodeURIComponent(TIPE_DIKECUALIKAN)}`,
      getFetcher
  )

  // Yang disimpan adalah nama (teks) dan ada perusahaan yang namanya sama
  // persis - didedup supaya tidak muncul opsi kembar.
  const opsiTujuanTerdaftar = [
      ...new Map(
          (dataTujuanTerdaftar?.data ?? [])
              .filter((item) => item?.name)
              .map((item) => [item.name.trim(), { label: item.name.trim(), value: item.name.trim(), perusahaan: item }])
      ).values(),
  ].sort((a, b) => a.label.localeCompare(b.label, 'id'))
  const { data: dataProvince } = useSWR("/api/location/provinces", getFetcher);
  const { data: dataRegency } = useSWR(provinceId ? `/api/location/cities?provinceId=${provinceId}` : null, getFetcher);
  const { data: dataDistrict } = useSWR(regencyId ? `/api/location/districts?regencyId=${regencyId}` : null, getFetcher);
  const { data: dataVillage } = useSWR(districtId ? `/api/location/villages?districtId=${districtId}` : null, getFetcher);

  const watchedFields = useWatch({ control, name: `perusahaanAsalMuatDanTujuanBongkar[${parentIndex}].perusahaanTujuanBongkar` });
  const watchedFieldsAll = useWatch({ control, name: `perusahaanAsalMuatDanTujuanBongkar` });
  const prevWatchedFields = useRef();
  const totalTransport = Array.isArray(watchedFields)
    ? watchedFields.reduce((total, field) => {
        const value = parseInt(field.jumlahDiBongkar) || 0;
        return total + value;
      }, 0)
    : 0;

  const totalTransportAll = watchedFieldsAll.reduce((sum, item) => {
    const subtotal = item.perusahaanTujuanBongkar.reduce((subSum, tujuan) => {
      return subSum + parseInt(tujuan.jumlahDiAngkut);
    }, 0);
    setValue("jumlahB3", sum + subtotal);
    return sum + subtotal;
  }, 0);

  useEffect(() => {
    if (dataTujuanBongkar?.data && watchedFields) {
      if (JSON.stringify(watchedFields) === JSON.stringify(prevWatchedFields.current)) {
        return;
      }
      prevWatchedFields.current = watchedFields;
      const updatedDetails = {};
      watchedFields.forEach((field, index) => {
        const detail = dataTujuanBongkar.data.find((item) => item.id === field?.perusahaanTujuanBongkarId);
        if (detail) {
          updatedDetails[index] = detail;
          setValue(`perusahaanAsalMuatDanTujuanBongkar[${parentIndex}].perusahaanTujuanBongkar[${index}].locationType`, detail.locationType);
          setValue(`perusahaanAsalMuatDanTujuanBongkar[${parentIndex}].perusahaanTujuanBongkar[${index}].longitudeTujuanBongkar`, detail.longitude);
          setValue(`perusahaanAsalMuatDanTujuanBongkar[${parentIndex}].perusahaanTujuanBongkar[${index}].latitudeTujuanBokar`, detail.latitude);
        }
      });
      setFilteredDetail(updatedDetails);
    }
  }, [watchedFields, dataTujuanBongkar, parentIndex, setValue]);

  function onCloseForm() {
    resetInline({
      namaPerusahaan: "",
      alamat: "",
      locationType: "",
      latitude: "",
      longitude: "",
    });
    onCloseModalForm();
  }

  async function onSubmitForm(formData) {
    try {
      formData.companyId = companyId;
      const response = await postFetcher("/api/company/tujuan-bongkar", formData);
      mutateTujuanBongkar();
      append({
        perusahaanTujuanBongkarId: response.id,
        locationType: response.locationType,
        longitudeTujuanBongkar: response.longitude,
        latitudeTujuanBongkar: response.latitude,
        jumlahPengangkutanB3: 0,
        jumlahDiAngkut: 0,
        jumlahDiBongkar: 0,
      });
      toast.success("Perusahaan tujuang bongkar berhasil ditambah!");
      onCloseForm();
    } catch (error) {
      isResponseErrorObject(error.response.data.message)
        ? Object.entries(error.response.data.message).forEach(([key, value]) => {
            toast.error(value);
          })
        : toast.error(error.response.data.message);
    }
  }

  return (
    <Card radius="sm">
      <CardHeader className="w-full flex justify-between">
        <div className="text-sm">
          Perusahaan Tujuan Bongkar <span className="text-danger ml-0.5">*</span>
        </div>
        <Button onPress={onOpenModalForm} color="primary" isIconOnly>
          <PlusIcon className="size-4" />
        </Button>
      </CardHeader>
      <CardBody>
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            onPress={() => append({ perusahaanTujuanBongkarId: "", locationType: "", longitudeTujuanBongkar: 0, latitudeTujuanBokar: 0, jumlahPengangkutanB3: 0, jumlahDiAngkut: 0, jumlahDiBongkar: 0 })}
            color="primary"
            startContent={<PlusIcon className="size-4" />}
          >
            Tambah Bongkar
          </Button>
          <span className="text-sm">
            Total Bongkar: <strong>{totalTransport}</strong>
          </span>
        </div>
        <div className="mt-3">
          <Table removeWrapper aria-label="table-tujuan-bongkar">
            <TableHeader>
              <TableColumn>Nama Perusahaan</TableColumn>
              <TableColumn>Detail</TableColumn>
              <TableColumn>Jumlah Pengangkutan B3 (KG)</TableColumn>
              <TableColumn>Jumlah Bongkar (KG)</TableColumn>
              <TableColumn>Aksi</TableColumn>
            </TableHeader>
            <TableBody>
              {fields.map((field, index) => (
                <TableRow key={index}>
                  <TableCell className="w-2/6">
                    <ControlledReactSelect
                      defaultValue={
                        field.perusahaanTujuanBongkarId
                          ? {
                              value: field.perusahaanTujuanBongkarId,
                              label: dataTujuanBongkar?.data?.find((item) => item.id === field.perusahaanTujuanBongkarId)?.namaPerusahaan || "Loading...",
                            }
                          : null
                      }
                      label=""
                      name={`perusahaanAsalMuatDanTujuanBongkar[${parentIndex}].perusahaanTujuanBongkar[${index}].perusahaanTujuanBongkarId`}
                      control={control}
                      options={dataTujuanBongkar?.data?.map((item) => ({ value: item.id, label: item.namaPerusahaan }))}
                      isLoading={isLoadingTujuanBongkar}
                    />
                  </TableCell>
                  <TableCell className="w-2/6">
                    {filteredDetail[index] ? (
                      <div className="text-xs">
                        <p>
                          <strong>Tipe Lokasi:</strong> {filteredDetail[index].locationType}
                        </p>
                        <p>
                          <strong>Longitude:</strong> {filteredDetail[index].longitude}
                        </p>
                        <p>
                          <strong>Latitude:</strong> {filteredDetail[index].latitude}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted">No detail available</p>
                    )}
                  </TableCell>
                  <TableCell className="w-1/6">
                    <ControlledInput type="number" label="" name={`perusahaanAsalMuatDanTujuanBongkar[${parentIndex}].perusahaanTujuanBongkar[${index}].jumlahDiAngkut`} isRequired={true} control={control} />
                  </TableCell>
                  <TableCell className="w-1/6">
                    <ControlledInput type="number" label="" name={`perusahaanAsalMuatDanTujuanBongkar[${parentIndex}].perusahaanTujuanBongkar[${index}].jumlahDiBongkar`} isRequired={true} control={control} />
                  </TableCell>
                  <TableCell>
                    <Button color="danger" size="sm" isIconOnly onClick={() => remove(index)}>
                      <TrashIcon className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardBody>

      <Modal size="3xl" isOpen={isOpenModalForm} scrollBehavior="inside" onOpenChange={onOpenChangeModalForm} onClose={onCloseForm} isDismissable={false} isKeyboardDismissDisabled={false}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Tambah Perusahaan Tujuan Bongkar</ModalHeader>
              <ModalBody>
                <form onSubmit={handleSubmitInline(onSubmitForm)}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    <ControlledReactSelect options={dataAsalMuat?.data?.map((item) => ({ value: item.id, label: item.namaPerusahaan }))} control={control} isLoading={isLoadingAsalMuat} label="Perusahaan Asal Muat" name="asalMuatId" />
                    <ControlledReactSelect
                      control={controlInline}
                      label="Perusahaan"
                      name="namaPerusahaan"
                      options={opsiTujuanTerdaftar}
                      onOptionChange={(opsi) => isiKolomDariPerusahaan(setValueInline, opsi?.perusahaan)}
                      isLoading={isLoadingTujuanTerdaftar}
                    />
                    <ControlledInput label="Alamat" name="alamat" type="text" isRequired={true} control={controlInline} />
                    <ControlledInput label="Latitude" name="latitude" type="text" isRequired={true} control={controlInline} />
                    <ControlledInput label="Longitude" name="longitude" type="text" isRequired={true} control={controlInline} />
                    <Select
                      {...registerInline("locationType")}
                      isRequired
                      variant="faded"
                      label="Tipe"
                      labelPlacement="outside"
                      placeholder="Pilih..."
                      color={errorsInline.locationType ? "danger" : "default"}
                      isInvalid={errorsInline.locationType}
                      errorMessage={errorsInline.locationType && errorsInline.locationType.message}
                    >
                      <SelectItem key="Factory">Factory</SelectItem>
                      <SelectItem key="Gudang">Gudang</SelectItem>
                      <SelectItem key="Pelabuhan">Pelabuhan</SelectItem>
                    </Select>
                    <Select
                      {...registerInline("provinceId")}
                      selectedKeys={nilaiFormInline?.provinceId ? [String(nilaiFormInline?.provinceId)] : []}
                      isRequired
                      disallowEmptySelection
                      variant="faded"
                      label="Provinsi"
                      labelPlacement="outside"
                      placeholder="Pilih..."
                      color={errorsInline.provinceId ? "danger" : "default"}
                      isInvalid={errorsInline.provinceId}
                      errorMessage={errorsInline.provinceId && errorsInline.provinceId.message}
                    >
                      {dataProvince?.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </Select>
                    <Select
                      {...registerInline("regencyId")}
                      selectedKeys={nilaiFormInline?.regencyId ? [String(nilaiFormInline?.regencyId)] : []}
                      isRequired
                      disallowEmptySelection
                      variant="faded"
                      label="Kabupaten"
                      labelPlacement="outside"
                      placeholder="Pilih..."
                      color={errorsInline.regencyId ? "danger" : "default"}
                      isInvalid={errorsInline.regencyId}
                      errorMessage={errorsInline.regencyId && errorsInline.regencyId.message}
                    >
                      {dataRegency?.map((item) => (
                        <SelectItem key={item.id}>{item.name}</SelectItem>
                      ))}
                    </Select>
                    <Select
                      {...registerInline("districtId")}
                      selectedKeys={nilaiFormInline?.districtId ? [String(nilaiFormInline?.districtId)] : []}
                      isRequired
                      disallowEmptySelection
                      variant="faded"
                      label="Kecamatan"
                      labelPlacement="outside"
                      placeholder="Pilih..."
                      color={errorsInline.districtId ? "danger" : "default"}
                      isInvalid={errorsInline.districtId}
                      errorMessage={errorsInline.districtId && errorsInline.districtId.message}
                    >
                      {dataDistrict?.map((item) => (
                        <SelectItem key={item.id}>{item.name}</SelectItem>
                      ))}
                    </Select>
                    <Select
                      {...registerInline("villageId")}
                      selectedKeys={nilaiFormInline?.villageId ? [String(nilaiFormInline?.villageId)] : []}
                      isRequired
                      disallowEmptySelection
                      variant="faded"
                      label="Kelurahan"
                      labelPlacement="outside"
                      placeholder="Pilih..."
                      color={errorsInline.villageId ? "danger" : "default"}
                      isInvalid={errorsInline.villageId}
                      errorMessage={errorsInline.villageId && errorsInline.villageId.message}
                    >
                      {dataVillage?.map((item) => (
                        <SelectItem key={item.id}>{item.name}</SelectItem>
                      ))}
                    </Select>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button onPress={handleSubmitInline(onSubmitForm)} isLoading={isSubmittingInline} isDisabled={isSubmittingInline} type="submit" color="primary">
                      Tambah
                    </Button>
                    <Button isDisabled={isSubmittingInline} color="danger" variant="faded" onPress={onClose}>
                      Batal
                    </Button>
                  </div>
                </form>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </Card>
  );
}
